import { hostIssueOf } from "../document/host-issues.ts";
import type { MapDocument, WorldDocument } from "../document/types.ts";
import { MAX_MAP_DIM } from "../effects/limits.ts";
import type { CompileContext } from "./context.ts";

/**
 * What the compile refuses of where a map sits and how it sounds (ADR-122 decisions 2.5 and 6): an atmosphere
 * naming music or ambience the game does not list, on any map; and a ready room whose host's link is broken or
 * which holds no arrival point (ADR-122 decision 2.2). A draft room's broken host is a link warning of the compile
 * (`CompiledWorld.linkWarnings`) and a warning of the Issues list, since a work in progress may be broken.
 */

/**
 * Refuses a map whose atmosphere names what the game does not list or holds a dim outside 0 to `MAX_MAP_DIM`, or a ready room
 * whose host is broken.
 *
 * @param world The world, for the host's map.
 * @param map The map.
 * @param context The compile context, whose `atmosphere` lists the names; with none, every name is refused.
 * @throws Error naming the map and the field, or the room and its host's broken link.
 */
export function checkMapPlace(world: WorldDocument, map: MapDocument, context: CompileContext): void {
  for (const key of ["music", "ambience"] as const) {
    const name = map.atmosphere?.[key];
    if (name !== undefined && !(context.atmosphere?.[key] ?? []).includes(name)) {
      throw new Error(`map ${map.id}: atmosphere.${key} ${name} is not ${key} the game lists`);
    }
  }
  const dim = map.atmosphere?.dim;
  if (dim !== undefined && !(dim >= 0 && dim <= MAX_MAP_DIM)) {
    throw new Error(`map ${map.id}: atmosphere.dim ${dim} is outside 0 to ${MAX_MAP_DIM}`);
  }
  if (map.draft !== true && map.host !== undefined) {
    const issue = hostIssueOf(world, map, context.arrivalType);
    if (issue !== null) {
      throw new Error(issue.message);
    }
    const arrivalType = context.arrivalType;
    if (arrivalType !== undefined && !map.layers.some((layer) => layer.kind === "entity" && layer.entities.some((entity) => entity.type === arrivalType))) {
      throw new Error(`map ${map.id}: a room holds no arrival point; place one`);
    }
  }
}

/**
 * Whether a map lies indoors: its light says so, or it is a room off the grid and its light says nothing (ADR-122 decision 6).
 *
 * @param map The map.
 * @returns True when indoors.
 */
export function mapIsIndoor(map: MapDocument): boolean {
  const light = map.atmosphere?.light;
  return light === "indoor" || (light === undefined && map.host !== undefined);
}
